import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import OpacityIcon from '@mui/icons-material/Opacity';
import ScienceIcon from '@mui/icons-material/Science';
import moment from 'moment/moment';
import QRCode from 'react-qr-code';
import Barcode from 'react-barcode';
import { useApi } from '../../ContextApi/ContextApi';
import './Template5.css';

const Template5 = ({ reportData, QRurl }) => {
  const [FontStyle, setFontStyles] = useState([]);
  const { Comment, isheader } = useApi();

  const currentDateTime = new Date();
  const username = (sessionStorage.getItem('FirstName') || '') + (sessionStorage.getItem('LastName') || '');

  // Helper function to convert UTC/server date strings to local browser time
  const formatLocalTime = (dateInput, formatPattern = 'D MMM YYYY hh:mm A') => {
    if (!dateInput) return '-';

    let formattedInput = dateInput;
    // If the date string lacks a 'Z' or offset indicator, append 'Z' so Moment treats it as UTC
    if (typeof dateInput === 'string' && !dateInput.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(dateInput)) {
      formattedInput = `${dateInput}Z`;
    }

    return moment.utc(formattedInput).local().format(formatPattern);
  };

  useEffect(() => {
    const fonts = JSON.parse(localStorage.getItem('FontStyle'));
    setFontStyles(fonts);
  }, []);

  const image = localStorage.getItem('LogoImage');
  const signatureImage = localStorage.getItem('SignatureImage');
  const qrCodeValue = QRurl;

  const groupByCategoryAndSubCategory = (data) => {
    return (data || []).reduce((result, item) => {
      const category = item.categorY_CODE;
      const subCategory = item.suB_CATEGORY_CODE;

      if (!result[category]) {
        result[category] = {
          categoryValue: item.categorY_VALUE,
          subCategories: {},
        };
      }

      if (!result[category].subCategories[subCategory]) {
        result[category].subCategories[subCategory] = {
          subCategoryValue: item.suB_CATEGORY_VALUE,
          items: [],
        };
      }

      result[category].subCategories[subCategory].items.push(item);
      return result;
    }, {});
  };

  const groupedData = groupByCategoryAndSubCategory(reportData);

  const formatTextWithLineBreaks = (text) => {
    if (!text) return '';
    return text.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < text.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const patient = reportData?.[0];
  const labName = reportData?.find((x) => x.laboratoryName !== '' && x.laboratoryName != null)?.laboratoryName;
  const mrNumber = `MR-${patient?.patientID || ''}-${sessionStorage.getItem('LabID') || ''}`;

  const bodyFont = FontStyle?.fontfamily ? FontStyle?.fontfamily : 'Georgia, serif';
  const bodyFontSize = FontStyle?.fontsize ? FontStyle?.fontsize : '11px';

  return (
    <>
      {/* ================= HEADER ================= */}
      <div className="t5-header">
        {isheader ? (
          <Grid container alignItems="center" className="t5-header-row">
            <Grid item xs={6}>
              <img
                className="t5-logo"
                src={'data:image/png;base64,' + image}
                alt="Lab Logo"
              />
            </Grid>
            <Grid item xs={2} style={{ textAlign: 'center' }}>
              <QRCode value={qrCodeValue} size={80} />
            </Grid>
            <Grid item xs={4}>
              <div className="t5-contact-info">
                <div><LocationOnIcon className="t5-icon" fontSize="inherit" /> {patient?.laboratoryAddress || '123 Health Avenue, Medical City'}</div>
                <div><PhoneIcon className="t5-icon" fontSize="inherit" /> {patient?.laboratoryContactNumber || '+123 456 7890'}</div>
                <div><EmailIcon className="t5-icon" fontSize="inherit" /> {patient?.laboratoryEmail}</div>
                <div><LanguageIcon className="t5-icon" fontSize="inherit" /> www.{(labName || 'lab').toLowerCase().replace(/\s+/g, '')}.com</div>
              </div>
            </Grid>
          </Grid>
        ) : ''}
        <hr className="t5-header-divider" />
      </div>

      {/* ================= PATIENT INFO + MR NUMBER / BARCODE ================= */}
      <div className="t5-patient-dev"> 
        <Grid
          container
          className="t5-patient-section"
          style={{ fontFamily: bodyFont, fontSize: bodyFontSize }}
        >
          <Grid item xs={7}>
            <div className="t5-info-row">
              <span className="t5-label">Patient Name</span><span className="t5-colon">:</span>
              <span className="t5-value">{patient?.patientName}</span>
            </div>
            <div className="t5-info-row">
              <span className="t5-label">Age / Gender</span><span className="t5-colon">:</span>
              <span className="t5-value">{patient?.age} Years / {patient?.sex}</span>
            </div>
            <div className="t5-info-row">
              <span className="t5-label">Referred By</span><span className="t5-colon">:</span>
              <span className="t5-value">{patient?.referredBy}</span>
            </div>
            <div className="t5-info-row">
              <span className="t5-label">Sample Type</span><span className="t5-colon">:</span>
              <span className="t5-value">{patient?.sampleType || '-'}</span>
            </div>
            <div className="t5-info-row">
              <span className="t5-label">Collected On</span><span className="t5-colon">:</span>
              <span className="t5-value">
                {patient?.collectedOn ? formatLocalTime(patient.collectedOn) : '-'}
              </span>
            </div>
            <div className="t5-info-row">
              <span className="t5-label">Reported On</span><span className="t5-colon">:</span>
              <span className="t5-value">
                {patient?.reportedOn
                  ? formatLocalTime(patient.reportedOn)
                  : formatLocalTime(patient?.createdAt)}
              </span>
            </div>
          </Grid>

          <Grid item xs={5}>
            <div className="t5-box">
              <div className="t5-box-header">MR NUMBER</div>
              <div className="t5-box-body t5-mrnumber">{mrNumber}</div>
            </div>
            <div className="t5-box" style={{ marginTop: '5px' }}>
              <div className="t5-box-header">BARCODE</div>
              <div className="t5-box-body t5-barcode-body">
                <Barcode
                  value={mrNumber}
                  height={40}
                  width={1.4}
                  fontSize={12}
                  margin={0}
                  displayValue={true}
                />
              </div>
            </div>
          </Grid>
        </Grid>
      </div>

      {/* ================= REPORT SECTIONS ================= */}
      <div className="t5-content">
        {Object.keys(groupedData).map((category, index) => {
          const subCategoryKeys = Object.keys(groupedData[category].subCategories);

          if (groupedData[category].categoryValue.toUpperCase() === 'URINE EXAMINATION REPORT') {
            const order = ['PHYSICAL EXAMINATION', 'CHEMICAL EXAMINATION', 'MICROSCOPIC EXAMINATION'];
            subCategoryKeys.sort((a, b) => {
              const subCategoryA = groupedData[category].subCategories[a].subCategoryValue.toUpperCase();
              const subCategoryB = groupedData[category].subCategories[b].subCategoryValue.toUpperCase();
              const indexA = order.indexOf(subCategoryA);
              const indexB = order.indexOf(subCategoryB);
              return (indexA === -1 ? order.length : indexA) - (indexB === -1 ? order.length : indexB);
            });
          }

          return (
            <div
              key={index}
              className="t5-category-block"
              style={{ fontFamily: bodyFont, fontSize: bodyFontSize }}
            >
              <div className="t5-category-title">
                <OpacityIcon className="t5-category-icon" fontSize="large" />
                {groupedData[category].categoryValue}
              </div>

              <TableContainer className="t5-table-container">
                <Table size="small" className="t5-table">
                  <TableHead>
                    <TableRow className="t5-table-head-row">
                      <TableCell className="t5-th">Test</TableCell>
                      <TableCell className="t5-th">Result</TableCell>
                      <TableCell className="t5-th">Unit</TableCell>
                      <TableCell className="t5-th">Reference Range</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subCategoryKeys.map((subCategory, subIndex) => (
                      <React.Fragment key={subIndex}>
                        <TableRow className="t5-subcategory-row">
                          <TableCell colSpan={4} className="t5-subcategory-cell">
                            {groupedData[category].subCategories[subCategory].subCategoryValue}
                          </TableCell>
                        </TableRow>
                        {groupedData[category].subCategories[subCategory].items.map((data, dIndex) => (
                          <React.Fragment key={dIndex}>
                            <TableRow className="t5-data-row">
                              <TableCell>{data.testName}</TableCell>
                              <TableCell className="t5-result-cell">{data.testResult}</TableCell>
                              <TableCell>{formatTextWithLineBreaks(data.unit)}</TableCell>
                              <TableCell>{formatTextWithLineBreaks(data.normalRange)}</TableCell>
                            </TableRow>
                            {data.comment && (
                              <TableRow>
                                <TableCell colSpan={4} className="t5-comment-cell">
                                  Comment: {data.comment}
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          );
        })}

        {Comment && (
          <div className="t5-interpretation-box" style={{ fontFamily: bodyFont }}>
            <span className="t5-interpretation-label">Interpretation:</span> {Comment}
          </div>
        )}
      </div>

      {/* ================= ELECTRONIC SIGNATURE ================= */}
      <div className="t5-signature-section" style={{ fontFamily: bodyFont }}>
        <Grid container justifyContent="flex-end" textAlign="center">
          <Grid item xs={4}>
            {signatureImage && (
              <img
                className="t5-signature-img"
                src={'data:image/png;base64,' + signatureImage}
                alt="Electronic Signature"
                style={{ maxHeight: '60px', marginBottom: '5px' }}
              />
            )}
            <div className="t5-signature-title" style={{ fontWeight: 'bold', fontSize: '12px' }}>
              Electronically Signed By:
            </div>
            <div className="t5-signature-note" style={{ fontSize: '10px', color: '#555' }}>
              This is a computer-generated report and does not require a physical signature.
            </div>
          </Grid>
        </Grid>
      </div>

      <hr className="t5-header-divider" />
      <div className="t5-footer-meta" style={{ fontFamily: bodyFont }}>
        <span>Printed at: &nbsp;{moment(currentDateTime).format('DD-MMM-YYYY hh:mm A')}</span>&nbsp;&nbsp;<span>Printed By: {username}</span>
      </div>

      <div className="t5-footer-copyright">
        <p>&copy; {new Date().getFullYear()} {labName}. All rights reserved.</p>
      </div>

      {/* ================= FOOTER ================= */}
      <div className="t5-footer">
        <div className="t5-footer-left">
          <ScienceIcon className="t5-flask-icon" />
          <span className="t5-footer-tagline">Accurate Results &bull; Timely Reports &bull; Trusted Care</span>
        </div>
        <div className="t5-footer-banner">
          Thank you for choosing<br /><strong>{labName || 'Our Laboratory'}</strong>
        </div>
      </div>
    </>
  );
};

export default Template5;